// Google Tag Manager utilities
import { ConsentState } from '../components/CookieConsent';

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

// Initialize dataLayer
window.dataLayer = window.dataLayer || [];

// GTM ID from environment
const GTM_ID = import.meta.env.VITE_GTM_ID;

// Push event to dataLayer
export const pushToDataLayer = (event: Record<string, any>) => {
  if (window.dataLayer) {
    window.dataLayer.push(event);
  }
};

// Initialize GTM after consent
export const initializeGTM = () => {
  if (!GTM_ID) {
    console.warn('GTM_ID not found in environment variables');
    return;
  }

  // Check if GTM is already loaded
  if (window.dataLayer?.find(item => item.event === 'gtm.js')) {
    console.log('GTM already initialized');
    return;
  }

  // Load GTM script dynamically
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  
  // Add to head
  document.head.appendChild(script);
  
  // Initialize dataLayer with GTM start event
  pushToDataLayer({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  });
  
  console.log('GTM initialized with ID:', GTM_ID);
};

// Update consent mode
export const updateConsentMode = (consent: ConsentState) => {
  // Google Analytics Consent Mode v2
  pushToDataLayer({
    event: 'consent_update',
    consent: {
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_storage: consent.marketing ? 'granted' : 'denied',
      ad_user_data: consent.marketing ? 'granted' : 'denied',
      ad_personalization: consent.marketing ? 'granted' : 'denied',
      functionality_storage: 'granted', // Always allow for necessary functionality
      security_storage: 'granted', // Always allow for security
      personalization_storage: consent.analytics ? 'granted' : 'denied',
    }
  });
  
  // Push consent state for custom tracking
  pushToDataLayer({
    event: 'cookie_consent_update',
    cookie_consent: {
      necessary: consent.necessary,
      analytics: consent.analytics,
      marketing: consent.marketing,
      timestamp: consent.timestamp
    }
  });
};

// Set initial consent state (before user interaction)
export const setDefaultConsentMode = () => {
  pushToDataLayer({
    event: 'consent_default',
    consent: {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
      personalization_storage: 'denied',
      wait_for_update: 500 // Wait 500ms for consent update
    }
  });
};

// Track custom events
export const trackEvent = (eventName: string, eventData?: Record<string, any>) => {
  pushToDataLayer({
    event: eventName,
    ...eventData
  });
};

// Track page view
export const trackPageView = (pageName: string, pageData?: Record<string, any>) => {
  pushToDataLayer({
    event: 'page_view',
    page_name: pageName,
    ...pageData
  });
};

// Track user actions
export const trackUserAction = (action: string, category?: string, label?: string, value?: number) => {
  pushToDataLayer({
    event: 'user_action',
    action: action,
    category: category || 'user_interaction',
    label: label,
    value: value
  });
};

// Track subscription events
export const trackSubscriptionEvent = (eventType: 'upgrade' | 'downgrade' | 'cancel', tier: string) => {
  pushToDataLayer({
    event: 'subscription_event',
    subscription_action: eventType,
    subscription_tier: tier,
    timestamp: new Date().toISOString()
  });
};

// Track sticky note events
export const trackStickyNoteEvent = (action: 'create' | 'update' | 'delete' | 'move', projectId?: string) => {
  pushToDataLayer({
    event: 'sticky_note_action',
    action: action,
    project_id: projectId,
    timestamp: new Date().toISOString()
  });
};

// Track AI usage
export const trackAIUsage = (suggestionsUsed: number, suggestionsLimit: number) => {
  pushToDataLayer({
    event: 'ai_usage',
    suggestions_used: suggestionsUsed,
    suggestions_limit: suggestionsLimit,
    usage_percentage: Math.round((suggestionsUsed / suggestionsLimit) * 100)
  });
};

// Initialize consent mode on page load
export const initializeConsentMode = () => {
  setDefaultConsentMode();
};